import React, { useEffect, memo } from "react";
import { compose } from "redux";
import { DragSource } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import classNames from "classnames";

import connectActions from "fantastic/lib/connectActions";
import connectProps from "fantastic/lib/connectProps";
import collectCells from "fantastic/lib/sections/collect";
import MouseTooltip from 'shared/components/MouseTooltip';
import ColumnTool from "fantastic/components/editor/sidebars/tools/Column";
import ItemTool from "fantastic/components/editor/sidebars/tools/Item";

import beginDragging from "fantastic/actions/beginDragging";
import endDragging from "fantastic/actions/endDragging";
import addToCollection from "fantastic/actions/addToCollection";
import moveSection from "fantastic/actions/moveSection";
import addSection from "fantastic/actions/addSection";
import moveItem from "fantastic/actions/moveItem";
import addItem from "fantastic/actions/addItem";
import Section from "./draggable/Section";
import Item from "./draggable/Item";

const typelize = str =>
  str.toLowerCase().replace(/^\w/, chr => chr.toUpperCase()) + "Entity";

const spec = {
  canDrag(props) {
    if (
      props.meta &&
      props.kind &&
      (props.kind === "spacer" || props.kind === "table") &&
      props.meta.resizing === true
    )
      return false;

    if (props.meta && props.meta.kind === "table") {
      let anyResizing = false;
      if (props.rows && props.rows.length > 0) {
        props.rows.forEach(row => {
          if (row.columns && row.columns.length > 0) {
            collectCells(row.columns, (_, cell) => {
              cell.items.forEach(item => {
                if (
                  item.meta.resizing === true ||
                  item.meta.selected === true
                ) {
                  anyResizing = true;
                }
              });
              return false;
            });
          }
        });
      }
      if (anyResizing) return false;
    }

    return true;
  },
  beginDrag(props, monitor, component) {
    const { id, meta, beginDragging } = props;
    beginDragging();
    return { id, meta };
  },
  endDrag(props, monitor) {
    const { target, fantasticCtrl = false } = window;

    props.endDragging();

    if (target) {
      switch (props.meta.type) {
        case "SECTION/NEW": {
          props.addSection({
            data: JSON.parse(JSON.stringify(props.data))
          });
          break;
        }
        case "SECTION": {
          if (fantasticCtrl || target.type) {
            const section = JSON.parse(
              JSON.stringify({
                contents: props.contents,
                full_width: props.full_width
              })
            );
            section.contents.forEach(column => {
              delete column.id;
              delete column.meta;
              if (column.items && column.items.length > 0)
                column.items.forEach(item => {
                  delete item.id;
                  delete item.meta;
                  if (
                    item.kind &&
                    item.kind === "table" &&
                    item.rows &&
                    item.rows.length > 0
                  ) {
                    item.rows.forEach(row => {
                      if (row.columns && row.columns.length > 0) {
                        row.columns.forEach(cell => {
                          delete cell.id;
                          delete cell.meta;
                          if (cell.items && cell.items.length > 0) {
                            cell.items.forEach(cellItem => {
                              delete cellItem.id;
                              delete cellItem.meta;
                            });
                          }
                        });
                      }
                    });
                  }
                });
            });

            if (target.type) {
              props.addToCollection({
                data: section,
                target,
                tag_list: [],
                type: typelize(props.meta.type)
              });
            } else {
              props.addSection({ data: section });
            }
          } else {
            props.moveSection({ id: props.id });
          }
          break;
        }
        case "ITEM/NEW": {
          props.addItem({
            data: JSON.parse(JSON.stringify(props.data))
          });
          break;
        }
        case "ITEM": {
          if ((fantasticCtrl) || target.type) { // fantasticCtrl && props.kind !== "unsubscrible"
            let item = JSON.parse(JSON.stringify(props));
            delete item.meta;
            delete item.id;
            if (
              item.kind &&
              item.kind === "table" &&
              item.rows &&
              item.rows.length > 0
            ) {
              item.rows.forEach(row => {
                if (row.columns && row.columns.length > 0) {
                  row.columns.forEach(cell => {
                    delete cell.id;
                    delete cell.meta;
                    if (cell.items && cell.items.length > 0) {
                      cell.items.forEach(cellItem => {
                        delete cellItem.id;
                        delete cellItem.meta;
                      });
                    }
                  });
                }
              });
            }
            if (target.type) {
              props.addToCollection({
                data: item,
                target,
                tag_list: [],
                type: typelize(props.meta.type)
              });
            } else {
              props.addItem({ data: item });
            }
          } else {
            props.moveItem({ id: props.id });
          }
          break;
        }
        default: {
          //console.log("NONE", props.meta.type);
        }
      }
    }

    [...document.querySelectorAll(".placeholder")].map(elm => elm.remove());
  },
  isDragging(props, monitor) {
    return monitor.getItem().id === props.id;
  }
};

const collect = (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  connectDragPreview: connect.dragPreview(),
  isDragging: monitor.isDragging()
});

const PreviewComponent = props => {
  return (
    <MouseTooltip style={{zIndex: 9999}} eventName="dragover">
     {props.itemType == 'ITEM/NEW' && <ItemTool {...props}/>}
     {props.itemType == 'SECTION/NEW' && <ColumnTool {...props}/>}
    </MouseTooltip>
  );
}

export default (type = "node") => Component => {
  const DraggableComponent = props => {
    //if (!props.meta.draggable) return <Component {...props} />;

    const { id } = props;
    const { isDragging, connectDragSource, connectDragPreview } = props;

    useEffect(() => {
      connectDragPreview(getEmptyImage());
    });

    const divClasses = classNames({
      "ftc-drag": isDragging && props.meta
    });

    if (
      props.itemType &&
      (props.itemType === "SECTION/NEW" || props.itemType === "ITEM/NEW")
    ) {
      return connectDragSource(
        <div className={divClasses}>
          {/* kew removes icon on moving
            (isDragging && !props.meta.fromEntity) && <PreviewComponent {...props} />*/}
          <Component {...props} />
        </div>
      );
    } else {
      switch (type) {
        case "SECTION": {
          return (
            <Section {...props} divClasses={divClasses} Component={Component} />
          );
        }
        case "ITEM": {
          return (
            <Item {...props} divClasses={divClasses} Component={Component} />
          );
        }
      }
    }
  };
  return compose(
    connectActions({
      moveSection,
      addSection,
      moveItem,
      addItem,
      addToCollection,
      beginDragging,
      endDragging
    })
  )(DragSource(type, spec, collect)(DraggableComponent));
};
